import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaSwitch from "../../../components/common/BriaSwitch/BriaSwitch.tsx";
import ConfirmationPopup from "../../../components/common/ConfirmationPopup/ConfirmationPopup.tsx";
import LoadingPlaceholder from "../../../components/common/LoadingPlaceholder/LoadingPlaceholder.tsx";
import RouterConstants from "../../../constants/RouterConstants.ts";
import { getSelectedOrganization } from "../../../helpers/localStorage.ts";
import useSecureNavigate from "../../../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../../../hooks/useStores.tsx";
import styles from "./MfaSettings.module.scss";

const MfaSettings = () => {
	const { t } = useTranslation("translation", { keyPrefix: "account.mfaSettings" });
	const [orgMfaStatus, setOrgMfaStatus] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isTogglingMfaStatus, setIsTogglingMfaStatus] = useState<boolean>(false);
	const [showMfaSetupPopup, setShowMfaSetupPopup] = useState<boolean>(false);
	const { authStore, uiStore } = useAppStore();
	const navigate = useSecureNavigate();

	const orgName = getSelectedOrganization()?.organization.name;

	const checkOrgMfaStatus = async () => {
		setOrgMfaStatus(await authStore.checkCurrentOrgMfa());
		setIsLoading(false);
	};

	useEffect(() => {
		checkOrgMfaStatus();
	}, []);

	const handleToggleChange = async () => {
		setIsTogglingMfaStatus(true);

		try {
			const changedOrgMfaStatus = !orgMfaStatus;

			await authStore.toggleOrgMfa(orgMfaStatus);

			const stateText = t(`${changedOrgMfaStatus ? "on" : "off"}`);

			if (changedOrgMfaStatus) {
				setShowMfaSetupPopup(!authStore.isMfaSetupCompleted);
				if (authStore.isMfaSetupCompleted) {
					await authStore.checkMfaSetupRequired();
				}
			} else {
				await authStore.checkMfaSetupRequired();
			}
			uiStore.showSnackBarAlert("success", t("successMessage", { state: stateText, org_name: orgName }), 5000);
			setOrgMfaStatus(changedOrgMfaStatus);
		} catch (e) {
			console.error(e);
		} finally {
			setIsTogglingMfaStatus(false);
		}
	};

	const handleMfaSetupConfirm = () => {
		navigate(RouterConstants.MFA_TOTP_SETUP.path);
	};

	return (
		<>
			<Box className={styles.tab}>
				<Box className={styles.container}>
					<Box className={styles.toggleRow}>
						<Typography className={styles.subtitle}>
							{t("enforceMfaText", { org_name: orgName })}
						</Typography>
						<LoadingPlaceholder
							loaderClassName={styles.toggleLoaderStyle}
							isLoading={isTogglingMfaStatus || isLoading}
						>
							<BriaSwitch checked={orgMfaStatus} onToggle={handleToggleChange} />
						</LoadingPlaceholder>
					</Box>
				</Box>
			</Box>
			{/* The Confirmation Popup for MFA checks */}
			<ConfirmationPopup
				open={showMfaSetupPopup}
				title={t("popup.title")}
				description={t("popup.description")}
				confirmButtonText={t("popup.confirmButton")}
				onClose={() => {}}
				hideCancel={true}
				hideCloseIcon={true}
				onClick={handleMfaSetupConfirm}
				loading={authStore.isLoading}
			/>
		</>
	);
};

export default observer(MfaSettings);
